import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Loader2, XCircle, Package } from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const { getAuthHeaders } = useAuth();
  const { fetchCart } = useCart();

  const checkPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        headers: getAuthHeaders()
      });

      setPaymentData(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        fetchCart();
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
      } else {
        setStatus("error");
      }
    }
  }, [sessionId, getAuthHeaders, fetchCart]);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId, checkPaymentStatus]);

  if (status === "loading") {
    return (
      <div className="container-main section-spacing text-center">
        <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
        <h1 className="text-h2 mb-4">Processing Your Order</h1>
        <p className="text-muted-foreground">
          Please wait while we confirm your payment...
        </p>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="container-main section-spacing text-center">
        <div className="max-w-lg mx-auto">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-h2 mb-4" data-testid="success-title">Thank You for Your Order!</h1>
          <p className="text-body-lg text-muted-foreground mb-8">
            Your payment of ${(paymentData?.amount_total / 100).toFixed(2)} has been received.
            We've sent a confirmation email with your order details.
          </p>

          <div className="bg-muted/30 rounded-xl p-6 mb-8 text-left">
            <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              What's Next?
            </h3>
            <ul className="space-y-3 text-muted-foreground">
              <li>• You'll receive an email confirmation shortly</li>
              <li>• We'll prepare your order for shipping</li>
              <li>• You can track your order in your account</li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/account">
              <Button className="btn-secondary" data-testid="view-orders-btn">
                View My Orders
              </Button>
            </Link>
            <Link to="/">
              <Button className="btn-primary" data-testid="continue-shopping-btn">
                Continue Shopping
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === "expired") {
    return (
      <div className="container-main section-spacing text-center">
        <div className="max-w-lg mx-auto">
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-12 h-12 text-yellow-600" />
          </div>
          <h1 className="text-h2 mb-4">Session Expired</h1>
          <p className="text-body-lg text-muted-foreground mb-8">
            Your checkout session has expired. Please try again.
          </p>
          <Link to="/cart">
            <Button className="btn-primary" data-testid="return-cart-btn">
              Return to Cart
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-main section-spacing text-center">
      <div className="max-w-lg mx-auto">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-12 h-12 text-red-600" />
        </div>
        <h1 className="text-h2 mb-4">Something Went Wrong</h1>
        <p className="text-body-lg text-muted-foreground mb-8">
          We couldn't verify your payment. Please contact support if you were charged.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/cart">
            <Button className="btn-secondary" data-testid="return-cart-error-btn">
              Return to Cart
            </Button>
          </Link>
          <a href="mailto:hello@athomemudbath.com">
            <Button className="btn-primary">Contact Support</Button>
          </a>
        </div>
      </div>
    </div>
  );
}
